import { type QueryKey } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { releasesGridConfig } from '@/features/releases/grid/gridConfig';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import { buildReleasesColumns } from './releasesColumns';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, ReleaseListItemDto } from '@flowqa/shared';

type ReleasesTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<ReleaseListItemDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: ReleaseListItemDto[]) => void;
  onQueryMeta?: (meta: { total: number; items: ReleaseListItemDto[] } | null) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
};

const ReleasesTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  noRowsText = 'No releases found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
}: ReleasesTableProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const defaultColumns = useMemo(
    () => buildReleasesColumns({ onProjectSelect: setSelectedProjectId }),
    [setSelectedProjectId],
  );

  const gridConfig = releasesGridConfig({
    orgId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'releases-table',
    initialSortModel,
    initialFilterModel,
  });

  return (
    <ServerDataTable<ReleaseListItemDto>
      queryPath="releases/query"
      {...gridConfig}
      fixedFilters={fixedFilters ?? gridConfig.fixedFilters}
      queryFn={queryFn ?? gridConfig.queryFn}
      queryKey={queryKey ?? gridConfig.queryKey}
      columns={defaultColumns}
      getRowId={(row) => row.id}
      token={session?.tokens.accessToken ?? null}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      pageSizeOptions={pageSizeOptions}
      initialPageSize={initialPageSize}
      disablePageSizeSelect={disablePageSizeSelect}
      noRowsText={noRowsText}
      disableCellFocus
      skeletonGetRowHeight={null}
      onRowsChange={onRowsChange}
      onQueryMeta={onQueryMeta}
      onQueryInput={onQueryInput}
      onDataUpdatedAt={onDataUpdatedAt}
      showLastUpdated={showLastUpdated}
      autoHeight
    />
  );
};

export const ReleasesTable = memo(ReleasesTableComponent);
